import { useEffect, useState, useRef } from 'react';
import { X, Download, FileText, Image, Video, Loader2 } from 'lucide-react';
import { fileAPI } from '@/api/fileAPI';
import { FileMeta } from '@/types/files';

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: FileMeta | null;
}

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  file,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen || !file) {
      if (urlRef.current) {
        window.URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
      setPreviewUrl(null);
      setError(null);
      return;
    }

    const loadPreview = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const url = await fileAPI.previewFile(file._id);
        urlRef.current = url;
        setPreviewUrl(url);
      } catch (err: any) {
        console.error('Failed to load preview:', err);
        // Check if it's a 404 or if the file ID format is invalid (old attachment system)
        if (err?.response?.status === 404 || err?.response?.status === 400) {
          setError('Preview not available for this file. Please download to view.');
        } else {
          setError('Failed to load file preview');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadPreview();

    // Cleanup: revoke object URL when component unmounts or file changes
    return () => {
      if (urlRef.current) {
        window.URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [isOpen, file?._id]);

  if (!isOpen || !file) return null;

  const handleDownload = async () => {
    try {
      await fileAPI.downloadFile(file._id, file.file_name);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const getFileIcon = () => {
    if (file.content_type?.startsWith('image/')) {
      return <Image className="w-8 h-8 text-blue-500" />;
    } else if (file.content_type?.startsWith('video/')) {
      return <Video className="w-8 h-8 text-purple-500" />;
    } else {
      return <FileText className="w-8 h-8 text-gray-500" />;
    }
  };

  const canPreviewInline = () => {
    if (!file.content_type) return false;
    return (
      file.content_type.startsWith('image/') ||
      file.content_type.startsWith('video/') ||
      file.content_type === 'application/pdf' ||
      file.content_type === 'text/plain' ||
      file.content_type.startsWith('text/')
    );
  };

  const renderPreview = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-gray-500">
          {getFileIcon()}
          <p className="mt-4 text-sm">{error}</p>
          <p className="mt-2 text-xs text-gray-400">Please use the download button instead</p>
        </div>
      );
    }

    if (!previewUrl) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-gray-500">
          {getFileIcon()}
          <p className="mt-4 text-sm">Preview not available</p>
        </div>
      );
    }

    // Image preview
    if (file.content_type?.startsWith('image/')) {
      return (
        <div className="flex items-center justify-center bg-gray-100 min-h-[400px] max-h-[80vh] overflow-auto">
          <img
            src={previewUrl}
            alt={file.file_name}
            className="max-w-full max-h-[80vh] object-contain"
          />
        </div>
      );
    }

    // Video preview
    if (file.content_type?.startsWith('video/')) {
      return (
        <div className="flex items-center justify-center bg-gray-100 min-h-[400px]">
          <video
            src={previewUrl}
            controls
            className="max-w-full max-h-[80vh]"
          >
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    // PDF preview
    if (file.content_type === 'application/pdf') {
      return (
        <div className="w-full h-[80vh]">
          <iframe
            src={previewUrl}
            className="w-full h-full border-0"
            title={file.file_name}
          />
        </div>
      );
    }

    // Text file preview
    if (file.content_type?.startsWith('text/') || file.content_type === 'application/json') {
      return (
        <div className="w-full h-[80vh] bg-gray-50 border rounded-lg overflow-auto">
          <iframe
            src={previewUrl}
            className="w-full h-full border-0"
            title={file.file_name}
          />
        </div>
      );
    }

    // Check for Office documents (DOCX, XLSX, PPTX, etc.) - cannot be previewed in browser
    const officeExtensions = ['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'odt', 'ods', 'odp'];
    const fileExtension = file.file_name.split('.').pop()?.toLowerCase();
    
    if (fileExtension && officeExtensions.includes(fileExtension)) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-gray-500">
          {getFileIcon()}
          <p className="mt-4 text-sm font-medium">Preview not available</p>
          <p className="mt-2 text-xs text-gray-400 max-w-md text-center">
            {fileExtension === 'docx' || fileExtension === 'doc' 
              ? 'Microsoft Word documents cannot be previewed directly in the browser. Please download the file to view it.'
              : `This ${fileExtension.toUpperCase()} file cannot be previewed directly in the browser. Please download the file to view it.`}
          </p>
          <button
            onClick={handleDownload}
            className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            Download to View
          </button>
        </div>
      );
    }

    // Fallback for other unsupported file types
    return (
      <div className="flex flex-col items-center justify-center h-96 text-gray-500">
        {getFileIcon()}
        <p className="mt-4 text-sm font-medium">Preview not available for this file type</p>
        <p className="mt-2 text-xs text-gray-400">Please download to view</p>
        <button
          onClick={handleDownload}
          className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          <Download className="w-4 h-4" />
          Download File
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <div className="flex-shrink-0">
                {getFileIcon()}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold text-gray-900 truncate">
                  {file.file_name}
                </h2>
                <p className="text-[10px] text-gray-500">
                  {new Date(file.upload_date).toLocaleDateString('en-GB', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleDownload}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                <Download className="w-3 h-3" />
                Download
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1.5"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Preview Content */}
          <div className="flex-1 overflow-auto p-4">
            {renderPreview()}
          </div>
        </div>
      </div>
    </div>
  );
};

